import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const LawnCareNotGettingLeads = () => {
  const symptoms = [
    "You're getting website traffic but no phone calls or form submissions",
    "Your quote form has less than 2% conversion rate",
    "Visitors spend less than 30 seconds on your homepage",
    "Your bounce rate is over 70%",
    "You rank for keywords but leads aren't coming in",
    "Competitors with worse websites are getting more business"
  ];

  const rootCauses = [
    { cause: "No clear call-to-action above the fold", impact: "Visitors don't know what to do next" },
    { cause: "Missing or buried phone number", impact: "Mobile users can't call easily" },
    { cause: "Quote form is too long or confusing", impact: "Potential leads abandon the form" },
    { cause: "No trust signals (reviews, certifications)", impact: "Visitors don't trust your business" },
    { cause: "Slow page load speed", impact: "Visitors leave before page loads" },
    { cause: "Not mobile-optimized", impact: "60%+ of traffic can't navigate properly" },
    { cause: "Generic content that doesn't address pain points", impact: "Visitors don't see you as the solution" },
    { cause: "No service area clarity", impact: "Visitors unsure if you serve their location" }
  ];

  const fixSteps = [
    { step: "Add a sticky call button for mobile visitors", details: "Make your phone number clickable and visible on every page" },
    { step: "Simplify your quote form to 4-5 fields max", details: "Name, phone, email, service needed, address - that's it" },
    { step: "Add Google reviews to your homepage", details: "Display your best 3-5 reviews with star ratings prominently" },
    { step: "Create a clear value proposition headline", details: "Tell visitors exactly what you do and why you're the best choice" },
    { step: "Add service area pages for each location", details: "Help local customers find you through location-specific content" },
    { step: "Speed up your website to under 3 seconds", details: "Compress images, enable caching, use a fast hosting provider" },
    { step: "Add urgency and scarcity messaging", details: "Limited availability, seasonal specials, booking fast messaging" }
  ];

  const faqs = [
    { question: "Why isn't my lawn care website getting leads?", answer: "The most common reasons are: no clear call-to-action, missing or hidden contact information, slow load times, poor mobile experience, lack of trust signals like reviews, and content that doesn't address customer pain points. Most lawn care websites fail at 2-3 of these simultaneously." },
    { question: "How many leads should a lawn care website generate?", answer: "A well-optimized lawn care website should convert 3-8% of visitors into leads. If you're getting 1,000 visitors per month, you should expect 30-80 form submissions or calls. Below 2% conversion indicates serious problems with your website." },
    { question: "What's the most important element for lead generation?", answer: "A clickable phone number and simple quote form above the fold are the two most critical elements. Over 60% of lawn care searches happen on mobile, and these visitors want to call immediately. Make it effortless." },
    { question: "How important are Google reviews on my website?", answer: "Extremely important. Websites with displayed reviews convert 270% better than those without. For lawn care specifically, homeowners want to see proof that you show up on time and do quality work." },
    { question: "Should I use a quote form or just a phone number?", answer: "Use both. Some customers prefer calling immediately, while others want to submit details and get a callback. A simple quote form (4-5 fields) plus a prominent phone number covers all preferences." },
    { question: "How fast should my lawn care website load?", answer: "Under 3 seconds on mobile. Every additional second of load time reduces conversions by 7%. Most lawn care websites load in 6-10 seconds, which is losing over half of potential leads." },
    { question: "Do I need service area pages?", answer: "Yes. Service area pages help you rank for 'lawn care near [city]' searches and reassure visitors that you actually serve their neighborhood. Each page should have unique content about that specific area." },
    { question: "What should my homepage headline say?", answer: "Your headline should clearly state what you do, who you serve, and what makes you different. Example: 'Professional Lawn Care for [City] Homeowners - Free Estimates, Same-Week Service.' Avoid vague headlines like 'Welcome to Our Website.'" },
    { question: "How do I know if my website is the problem?", answer: "If you're getting traffic but no leads, the website is the problem. Use Google Analytics to check bounce rate (should be under 60%), time on site (should be over 1 minute), and conversion rate (should be over 2%)." },
    { question: "Can I fix my website myself or do I need help?", answer: "Basic fixes like adding a phone number or simplifying a form can be done yourself. However, comprehensive optimization including speed, mobile experience, conversion design, and SEO typically requires professional help to do correctly." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/lawn-care-website-design/", label: "Lawn Care Website Design" },
    { to: "/growth-add-ons/gbp/", label: "Google Business Profile Optimization" },
    { to: "/service-pages-that-rank/", label: "Service Pages That Rank" }
  ];

  return (
    <>
      <SEO
        title="Lawn Care Website Not Getting Leads? Here's the Fix | Leaf Rush Marketing"
        description="Discover why your lawn care website isn't generating leads and get a step-by-step fix guide. Convert more visitors into paying customers."
        url="https://www.leafrushmarketing.com/website-fixes/lawn-care-website-not-getting-leads/"
      />
      <PageSchema
        url="https://www.leafrushmarketing.com/website-fixes/lawn-care-website-not-getting-leads/"
        title="Lawn Care Website Not Getting Leads? Here's the Fix"
        description="Discover why your lawn care website isn't generating leads and get a step-by-step fix guide."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Lawn Care Website Not Getting Leads? Here's the Fix
            </h1>
            <p className="text-xl text-muted-foreground">
              Your website looks fine, but the phone isn't ringing. Here's why—and exactly how to fix it.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Website Has a Lead Problem" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Your Website Isn't Converting</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Fix Your Lead Generation" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Biggest Mistake: Pretty But Not Profitable</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Many lawn care business owners invest thousands in a beautiful website, only to discover it doesn't generate any leads. The problem? <strong>Web designers focus on aesthetics, not conversions.</strong>
              </p>
              <p>
                A lead-generating lawn care website needs specific elements that most designers don't include: strategic CTA placement, trust signals, mobile-first quote forms, and speed optimization. Without these, even the most stunning website will fail to convert visitors into customers.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, every website we build is designed for one purpose: getting your phone to ring. We combine proven conversion principles with lawn care industry expertise to create websites that actually generate leads.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get Your Free Website Audit" />
        </div>
      </article>
    </>
  );
};

export default LawnCareNotGettingLeads;
